import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, List, ListItem, ListItemText, MenuItem,
  Stack, TextField, Typography, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { StatCard, SectionHeader } from '../components/ui';
import { computeConsistencyScore, computeFocusScore, getTotalStudyHours, getWeekStart } from '../utils/schema';

export default function FocusPage() {
  const focus = useGrowthPlannerStore((s) => s.focus);
  const routes = useGrowthPlannerStore((s) => s.routes);
  const logStudySession = useGrowthPlannerStore((s) => s.logStudySession);
  const addWeeklyGoal = useGrowthPlannerStore((s) => s.addWeeklyGoal);
  const addMonthlyGoal = useGrowthPlannerStore((s) => s.addMonthlyGoal);
  const deleteWeeklyGoal = useGrowthPlannerStore((s) => s.deleteWeeklyGoal);
  const deleteMonthlyGoal = useGrowthPlannerStore((s) => s.deleteMonthlyGoal);

  const [studyDialog, setStudyDialog] = useState(false);
  const [weeklyDialog, setWeeklyDialog] = useState(false);
  const [monthlyDialog, setMonthlyDialog] = useState(false);
  const [studyForm, setStudyForm] = useState({ hours: 1, routeId: '', notes: '' });
  const [weeklyForm, setWeeklyForm] = useState({ title: '', targetHours: 5 });
  const [monthlyForm, setMonthlyForm] = useState({ title: '', targetHours: 20 });

  const weekStart = getWeekStart();
  const weeklyHours = getTotalStudyHours(focus.studyLog, weekStart);
  const monthStart = new Date().toISOString().slice(0, 7);
  const monthlyHours = focus.studyLog
    .filter((e) => e.date.startsWith(monthStart))
    .reduce((s, e) => s + (e.hours || 0), 0);

  const currentWeeklyGoals = focus.weeklyGoals.filter((g) => g.weekStart === weekStart);
  const currentMonthlyGoals = focus.monthlyGoals.filter((g) => g.month === monthStart);
  const focusScore = computeFocusScore(focus.weeklyGoals);
  const consistency = computeConsistencyScore(focus.studyLog);

  const handleLogStudy = () => {
    logStudySession({
      hours: Number(studyForm.hours) || 1,
      routeId: studyForm.routeId || null,
      notes: studyForm.notes,
    });
    setStudyForm({ hours: 1, routeId: '', notes: '' });
    setStudyDialog(false);
  };

  const recentLog = [...focus.studyLog].reverse().slice(0, 10);

  return (
    <Box>
      <SectionHeader
        title="Progreso & Focus"
        subtitle="Metas, rachas y consistencia para mantener el enfoque"
        action={
          <Button variant="contained" onClick={() => setStudyDialog(true)}>
            Registrar estudio
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Focus Score" value={`${focusScore}%`} color="#6366f1" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Consistencia (30d)" value={`${consistency}%`} color="#8b5cf6" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Racha actual" value={`${focus.streak?.current || 0} días`} subtitle={`Mejor: ${focus.streak?.longest || 0}`} color="#f97316" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Horas semana" value={`${weeklyHours}h`} subtitle={`${monthlyHours}h este mes`} color="#06b6d4" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Metas semanales</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setWeeklyDialog(true)}>Agregar</Button>
              </Stack>
              {currentWeeklyGoals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Define metas para esta semana.</Typography>
              ) : (
                currentWeeklyGoals.map((goal) => {
                  const pct = goal.targetHours ? Math.min(100, Math.round((goal.completedHours / goal.targetHours) * 100)) : 0;
                  return (
                    <Box key={goal.id} sx={{ mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={500}>{goal.title}</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption">{goal.completedHours || 0}/{goal.targetHours}h</Typography>
                          <IconButton size="small" onClick={() => deleteWeeklyGoal(goal.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                      </Stack>
                      <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Metas mensuales</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setMonthlyDialog(true)}>Agregar</Button>
              </Stack>
              {currentMonthlyGoals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Define metas para este mes.</Typography>
              ) : (
                currentMonthlyGoals.map((goal) => {
                  const pct = goal.targetHours ? Math.min(100, Math.round((goal.completedHours / goal.targetHours) * 100)) : 0;
                  return (
                    <Box key={goal.id} sx={{ mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={500}>{goal.title}</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption">{goal.completedHours || 0}/{goal.targetHours}h</Typography>
                          <IconButton size="small" onClick={() => deleteMonthlyGoal(goal.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                      </Stack>
                      <LinearProgress variant="determinate" value={pct} color="secondary" sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Registro de estudio</Typography>
              {recentLog.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Registra tus sesiones de estudio para medir progreso.</Typography>
              ) : (
                <List dense disablePadding>
                  {recentLog.map((entry) => {
                    const route = routes.find((r) => r.id === entry.routeId);
                    return (
                      <ListItem key={entry.id} disableGutters sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={`${entry.hours}h — ${route?.name || 'General'}`}
                          secondary={`${entry.date}${entry.notes ? ` · ${entry.notes}` : ''}`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={studyDialog} onClose={() => setStudyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Registrar sesión de estudio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Horas" type="number" value={studyForm.hours} onChange={(e) => setStudyForm({ ...studyForm, hours: e.target.value })} fullWidth />
            <TextField select label="Ruta (opcional)" value={studyForm.routeId} onChange={(e) => setStudyForm({ ...studyForm, routeId: e.target.value })} fullWidth>
              <MenuItem value="">General</MenuItem>
              {routes.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </TextField>
            <TextField label="Notas" value={studyForm.notes} onChange={(e) => setStudyForm({ ...studyForm, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudyDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleLogStudy}>Registrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={weeklyDialog} onClose={() => setWeeklyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Meta semanal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" value={weeklyForm.title} onChange={(e) => setWeeklyForm({ ...weeklyForm, title: e.target.value })} fullWidth />
            <TextField label="Horas objetivo" type="number" value={weeklyForm.targetHours} onChange={(e) => setWeeklyForm({ ...weeklyForm, targetHours: Number(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWeeklyDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => { addWeeklyGoal(weeklyForm); setWeeklyDialog(false); setWeeklyForm({ title: '', targetHours: 5 }); }}>Agregar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={monthlyDialog} onClose={() => setMonthlyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Meta mensual</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" value={monthlyForm.title} onChange={(e) => setMonthlyForm({ ...monthlyForm, title: e.target.value })} fullWidth />
            <TextField label="Horas objetivo" type="number" value={monthlyForm.targetHours} onChange={(e) => setMonthlyForm({ ...monthlyForm, targetHours: Number(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonthlyDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => { addMonthlyGoal(monthlyForm); setMonthlyDialog(false); setMonthlyForm({ title: '', targetHours: 20 }); }}>Agregar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
